import { Spacer } from "@hackthenorth/north";
import {
  ArrowLeftIcon,
  MinusSmallIcon,
  PlusSmallIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { Button, Select, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";
import { BaseRoute, MentorRoute } from "src/shared/constants/route";

import { useAddMentorShiftContext } from "../../shared/contexts/AddMentorShiftContext";

import "twin.macro";
import { convertDayToDate } from "../../shared/contexts/constants";

const AddMentorShiftPage = () => {
  const navigate = useNavigate();

  const [shiftNameTouched, setShiftNameTouched] = useState(false);
  const [locationTouched, setLocationTouched] = useState(false);
  const [timeFrameTouched, setTimeFrameTouched] = useState(false);

  const {
    shiftName,
    shiftDateStart,
    shiftDateEnd,
    timeStart,
    timeEnd,
    location,
    requiredMentors,

    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateRequiredMentors,

    createShift,
  } = useAddMentorShiftContext();

  useEffect(() => {
    if (!timeStart) {
      updateTimeStart("00:00");
    }

    if (!timeEnd) {
      updateTimeEnd("00:00");
    }

    if (!shiftDateStart) {
      updateShiftDateStart("Friday");
    }

    if (!shiftDateEnd) {
      updateShiftDateEnd("Friday");
    }
  }, [
    timeStart,
    timeEnd,
    shiftDateStart,
    shiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateShiftDateStart,
    updateShiftDateEnd,
  ]);

  const validTimeFrame = (
    startDate: string,
    endDate: string,
    startTime: string,
    endTime: string
  ) => {
    const startDateTime = moment(
      `${convertDayToDate(startDate)} ${startTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const endDateTime = moment(
      `${convertDayToDate(endDate)} ${endTime}`,
      "YYYY-MM-DD HH:mm"
    );

    return startDateTime.isBefore(endDateTime);
  };

  // Time Start and Time End selection choices
  const timeChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push(time);
      }
    }
    return times;
  }, []);

  const handleAddItem = useCallback(
    (e) => {
      e.preventDefault();

      setShiftNameTouched(true);
      setLocationTouched(true);
      setTimeFrameTouched(true);

      if (
        shiftName.length < 2 ||
        !shiftDateStart ||
        !shiftDateEnd ||
        !timeStart ||
        !timeEnd ||
        !location ||
        !validTimeFrame(shiftDateStart, shiftDateEnd, timeStart, timeEnd)
      ) {
        return;
      }

      createShift().then(() => {
        navigate(`${BaseRoute.MENTOR}/${MentorRoute.SHIFTS}`);
      });
    },
    [
      createShift,
      navigate,
      shiftName,
      shiftDateStart,
      shiftDateEnd,
      timeStart,
      timeEnd,
      location,
    ]
  );

  return (
    <PageWrapper>
      <AddMentorShiftContainer>
        <div tw="flex flex-row gap-4 items-center">
          <ArrowLeftIcon tw="h-16 font-bold text-text-secondary cursor-pointer" />
          <Text
            type="button"
            tw="font-bold text-text-secondary cursor-pointer"
            color="secondary"
            onClick={() =>
              navigate(`${BaseRoute.MENTOR}/${MentorRoute.SHIFTS}`)
            }
          >
            Back to Mentor Shifts
          </Text>
        </div>

        {/* </Button> */}
        <Spacer height={24} />

        <Text type="h2">Add a shift</Text>
        <Spacer height={8} />

        <Text type="body1">
          Add, edit, or delete shifts by filling the form below.
        </Text>
        <Spacer height={24} />

        {/* FORM */}
        <StyledForm onSubmit={handleAddItem}>
          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift name
            </Text>

            <TextInput
              type="text"
              placeholder="Enter a name..."
              tw="text-text-secondary font-medium h-48 w-full!"
              onChange={(e) => updateShiftName(e.target.value)}
              value={shiftName}
              error={
                shiftNameTouched && shiftName.length <= 2
                  ? "Shift name is too short"
                  : ""
              }
              onBlur={() => setShiftNameTouched(true)}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Start Date
              </Text>

              <Select
                tw="h-48 text-text-secondary font-medium"
                fullWidth
                onChange={(e: any) => updateShiftDateStart(e.target.value)}
                value={shiftDateStart}
                placeholder="Enter a start date"
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                <Select.Option value={"Friday"}>Friday</Select.Option>
                <Select.Option value={"Saturday"}>Saturday</Select.Option>
                <Select.Option value={"Sunday"}>Sunday</Select.Option>
              </Select>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                End Date
              </Text>

              <Select
                tw="h-48 text-text-secondary font-medium"
                fullWidth
                onChange={(e: any) => updateShiftDateEnd(e.target.value)}
                value={shiftDateEnd}
                placeholder="Enter an end date"
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                <Select.Option value={"Friday"}>Friday</Select.Option>
                <Select.Option value={"Saturday"}>Saturday</Select.Option>
                <Select.Option value={"Sunday"}>Sunday</Select.Option>
              </Select>
            </InputContainer>
          </div>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time Start
              </Text>

              <Select
                fullWidth
                tw="h-48 text-text-secondary font-medium"
                value={timeStart}
                onChange={(e) => {
                  updateTimeStart(e.target.value);
                }}
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? "Time Start must be before Time End"
                    : ""
                }
              >
                {timeChoices.map((time) => (
                  <Select.Option key={time} value={time}>
                    {time}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time End
              </Text>

              <Select
                fullWidth
                tw="h-48 text-text-secondary font-medium"
                value={timeEnd}
                onChange={(e) => {
                  updateTimeEnd(e.target.value);
                }}
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                {timeChoices.map((time) => (
                  <Select.Option key={time} value={time}>
                    {time}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>
          </div>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Location
            </Text>

            <TextInput
              type="text"
              placeholder="Enter location"
              tw="text-text-secondary font-medium h-48 w-full!"
              width={"700px"}
              onChange={(e) => updateLocation(e.target.value)}
              error={
                locationTouched && location === "" ? "Location is required" : ""
              }
              onBlur={() => setLocationTouched(true)}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer>
              <Text type="body1" tw="font-bold">
                Required Mentors
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    if (requiredMentors > 0)
                      updateRequiredMentors(requiredMentors - 1);
                  }}
                  height={24}
                />
                <div style={{ width: "48px" }}>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={requiredMentors}
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    updateRequiredMentors(requiredMentors + 1);
                  }}
                  height={24}
                />
              </div>
            </InputContainer>
          </div>

          <Button color="primary" type="submit">
            <Text>Add Item</Text>
          </Button>
        </StyledForm>

        <Spacer height={64} />
      </AddMentorShiftContainer>
    </PageWrapper>
  );
};

const AddMentorShiftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  max-width: 700px;
`;

export default AddMentorShiftPage;
